type NewRelicErrorHandler = (err: unknown) => boolean;

type NewRelicApi = {
    setErrorHandler?: (filterCallback: NewRelicErrorHandler) => void;
    addPageAction?: (name: string, attrs?: Record<string, unknown>) => void;
};

const getNewRelic = (): NewRelicApi | undefined => {
    /* istanbul ignore next -- defensive guard for SSR; not exercised in jsdom tests */
    if (typeof window === 'undefined') return undefined;
    return (window as unknown as { newrelic?: NewRelicApi }).newrelic;
};

export const NOISE_FILTER_SUPPRESSED_ACTION = 'NoiseFilterSuppressed';

type MessageNoiseEntry = {
    kind: 'message';
    pattern: RegExp;
    reason: string;
};

type DomainNoiseEntry = {
    kind: 'domain';
    pattern: RegExp;
    reason: string;
};

type NoiseEntry = MessageNoiseEntry | DomainNoiseEntry;

export const KNOWN_NOISE_PATTERNS: NoiseEntry[] = [
    { kind: 'message', pattern: /MetaMask/i, reason: 'metamask-extension' },
    { kind: 'message', pattern: /tradeInYearsFailure - Failing getting YEARS/, reason: 'trade-in-years-failure' },
    { kind: 'domain', pattern: /dealertrack\.com/i, reason: 'dealertrack-document-upload' },
    { kind: 'domain', pattern: /vinccrmnp\.cloud/i, reason: 'customer-card-pbc' }
];

const hasStringProp = (value: unknown, prop: string): value is Record<string, string> =>
    typeof value === 'object' && value !== null && typeof (value as Record<string, unknown>)[prop] === 'string';

const getErrorMessage = (err: unknown): string => {
    if (hasStringProp(err, 'message')) return err.message;
    if (typeof err === 'string') return err;
    return String(err ?? '');
};

const getErrorStack = (err: unknown): string => (hasStringProp(err, 'stack') ? err.stack : '');

const findKnownNoiseEntry = (err: unknown): NoiseEntry | undefined => {
    const message = getErrorMessage(err);
    const stack = getErrorStack(err);
    return KNOWN_NOISE_PATTERNS.find((entry) =>
        entry.kind === 'message' ? entry.pattern.test(message) : entry.pattern.test(message) || entry.pattern.test(stack)
    );
};

export const isKnownNoise = (err: unknown): boolean => !!findKnownNoiseEntry(err);

export const registerNewRelicNoiseFilter = (): void => {
    const nr = getNewRelic();
    if (!nr || typeof nr.setErrorHandler !== 'function') return;
    nr.setErrorHandler((err) => {
        const entry = findKnownNoiseEntry(err);
        if (!entry) return false;
        if (typeof nr.addPageAction === 'function') {
            nr.addPageAction(NOISE_FILTER_SUPPRESSED_ACTION, { reason: entry.reason, message: getErrorMessage(err) });
        }
        return true;
    });
};
